// ─────────────────────────────────────────
// EditRoundModal.js — edit or delete a round
// ─────────────────────────────────────────
// A bottom sheet that slides up from the bottom of the screen.
// Used in both Feed.js and Passport.js.
//
// Props:
//   round    — the round object being edited
//   onSave   — called with the updated round after saving
//   onDelete — called with the round id after deleting
//   onClose  — called when the user dismisses without saving

import { useState } from 'react'
import { supabase } from '../lib/supabase'

export default function EditRoundModal({ round, onSave, onDelete, onClose }) {
  // Pre-fill the form with the round's existing values
  const [form, setForm] = useState({
    score:    String(round.score || ''),
    par:      String(round.par || '72'),
    fairways: String(round.fairways ?? ''),
    gir:      String(round.gir ?? ''),
    putts:    String(round.putts ?? ''),
    caption:  round.caption || ''
  })

  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false) // shows the "are you sure?" step
  const [error, setError] = useState(null)

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  // ── SAVE ─────────────────────────────────────────────────────
  async function handleSave() {
    if (!form.score) return
    setSaving(true)
    setError(null)

    const updates = {
      score:    parseInt(form.score),
      par:      parseInt(form.par) || null,
      fairways: form.fairways !== '' ? parseInt(form.fairways) : null,
      gir:      form.gir !== ''      ? parseInt(form.gir)      : null,
      putts:    form.putts !== ''    ? parseInt(form.putts)    : null,
      caption:  form.caption.trim() || null
    }

    const { error } = await supabase
      .from('rounds')
      .update(updates)
      .eq('id', round.id)

    if (error) {
      setError(error.message)
      setSaving(false)
      return
    }

    // Pass the merged round back up so the parent can update its list in-place
    onSave({ ...round, ...updates })
  }

  // ── DELETE ───────────────────────────────────────────────────
  async function handleDelete() {
    setDeleting(true)

    const { error } = await supabase
      .from('rounds')
      .delete()
      .eq('id', round.id)

    if (error) {
      setError(error.message)
      setDeleting(false)
      return
    }

    onDelete(round.id)
  }

  // ── RENDER ───────────────────────────────────────────────────
  return (
    // Backdrop — tap outside the sheet to close
    <div style={styles.backdrop} onClick={onClose}>

      {/* The sheet itself — stopPropagation prevents backdrop click when tapping inside */}
      <div style={styles.sheet} onClick={e => e.stopPropagation()}>

        {/* Drag handle */}
        <div style={styles.handle} />

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.title}>Edit Round</div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.courseLine}>{round.course_name}</div>

        {/* ── SCORE + PAR ── */}
        <div style={styles.row}>
          <div style={styles.fieldHalf}>
            <label style={styles.label}>Score *</label>
            <input
              style={{ ...styles.input, fontSize: 28, textAlign: 'center', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800 }}
              type="number" min="18" max="200"
              value={form.score}
              onChange={e => update('score', e.target.value)}
            />
          </div>
          <div style={styles.fieldHalf}>
            <label style={styles.label}>Par</label>
            <input
              style={{ ...styles.input, fontSize: 18, textAlign: 'center', fontWeight: 600 }}
              type="number" min="54" max="74"
              value={form.par}
              onChange={e => update('par', e.target.value)}
            />
          </div>
        </div>

        {/* ── STATS ── */}
        <div style={styles.statsRow}>
          <div style={styles.statField}>
            <label style={styles.label}>FIR</label>
            <input
              style={{ ...styles.input, textAlign: 'center' }}
              type="number" min="0" max="18" placeholder="—"
              value={form.fairways}
              onChange={e => update('fairways', e.target.value)}
            />
          </div>
          <div style={styles.statField}>
            <label style={styles.label}>GIR</label>
            <input
              style={{ ...styles.input, textAlign: 'center' }}
              type="number" min="0" max="18" placeholder="—"
              value={form.gir}
              onChange={e => update('gir', e.target.value)}
            />
          </div>
          <div style={styles.statField}>
            <label style={styles.label}>Putts</label>
            <input
              style={{ ...styles.input, textAlign: 'center' }}
              type="number" min="18" max="72" placeholder="—"
              value={form.putts}
              onChange={e => update('putts', e.target.value)}
            />
          </div>
        </div>

        {/* ── CAPTION ── */}
        <div style={styles.field}>
          <label style={styles.label}>Trash Talk</label>
          <textarea
            style={{ ...styles.input, minHeight: 64, resize: 'none' }}
            placeholder="Update your caption..."
            value={form.caption}
            onChange={e => update('caption', e.target.value)}
            maxLength={280}
          />
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {/* ── SAVE BUTTON ── */}
        <button
          style={{ ...styles.saveBtn, opacity: saving ? 0.7 : 1 }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>

        {/* ── DELETE — two-step confirm ── */}
        {!confirmDelete ? (
          <button style={styles.deleteBtn} onClick={() => setConfirmDelete(true)}>
            Delete Round
          </button>
        ) : (
          <div style={styles.confirmRow}>
            <span style={styles.confirmText}>Are you sure? This can't be undone.</span>
            <button
              style={{ ...styles.confirmYes, opacity: deleting ? 0.7 : 1 }}
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Yes, delete'}
            </button>
            <button style={styles.confirmNo} onClick={() => setConfirmDelete(false)}>
              Cancel
            </button>
          </div>
        )}

      </div>
    </div>
  )
}

// ── STYLES ───────────────────────────────────────────────────

const styles = {
  // Semi-transparent backdrop covers the whole screen
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 500,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
  },

  sheet: {
    width: '100%',
    maxWidth: 600,
    margin: '0 auto',
    background: 'white',
    borderRadius: 20,
    padding: '20px 20px 32px',
    maxHeight: '90vh',
    overflowY: 'auto'
  },

  handle: {
    width: 40, height: 4,
    background: '#dde4df',
    borderRadius: 4,
    margin: '0 auto 16px'
  },

  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 4
  },
  title: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 22, color: '#0e1f13', letterSpacing: 0.5
  },
  closeBtn: {
    background: '#f4f7f5', border: 'none', borderRadius: '50%',
    width: 32, height: 32, fontSize: 14, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7c6e'
  },

  courseLine: {
    fontSize: 13, color: '#6b7c6e', marginBottom: 20,
    fontStyle: 'italic'
  },

  row: { display: 'flex', gap: 10, marginBottom: 14 },
  fieldHalf: { flex: 1, display: 'flex', flexDirection: 'column', gap: 6 },
  statsRow: { display: 'flex', gap: 10, marginBottom: 14 },
  statField: { flex: 1, display: 'flex', flexDirection: 'column', gap: 6 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 },

  label: {
    fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
    textTransform: 'uppercase', color: '#6b7c6e'
  },
  input: {
    background: '#f4f7f5', border: '1.5px solid #e2ebe4', borderRadius: 12,
    padding: '11px 12px', fontSize: 15, color: '#0e1f13', outline: 'none',
    fontFamily: "'Barlow', sans-serif", width: '100%', boxSizing: 'border-box'
  },

  error: {
    background: 'rgba(232,64,64,0.1)', border: '1px solid rgba(232,64,64,0.3)',
    color: '#c0392b', borderRadius: 10, padding: '10px 14px',
    fontSize: 13, marginBottom: 12
  },

  saveBtn: {
    width: '100%', background: '#c8f135', color: '#0e1f13',
    border: 'none', borderRadius: 12, padding: '16px',
    fontSize: 16, fontWeight: 900, cursor: 'pointer',
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1,
    marginBottom: 10
  },

  deleteBtn: {
    width: '100%', background: 'none', color: '#c0392b',
    border: '1.5px solid rgba(192,57,43,0.25)', borderRadius: 12,
    padding: '13px', fontSize: 14, fontWeight: 700,
    cursor: 'pointer', fontFamily: "'Barlow', sans-serif"
  },

  confirmRow: {
    background: 'rgba(192,57,43,0.06)', borderRadius: 12,
    padding: '14px', display: 'flex', flexDirection: 'column', gap: 8
  },
  confirmText: { fontSize: 13, color: '#c0392b', fontWeight: 600, textAlign: 'center' },
  confirmYes: {
    background: '#c0392b', color: 'white', border: 'none',
    borderRadius: 10, padding: '11px', fontSize: 14,
    fontWeight: 700, cursor: 'pointer', fontFamily: "'Barlow', sans-serif"
  },
  confirmNo: {
    background: 'none', color: '#6b7c6e', border: '1px solid #e2ebe4',
    borderRadius: 10, padding: '11px', fontSize: 14,
    fontWeight: 600, cursor: 'pointer', fontFamily: "'Barlow', sans-serif"
  }
}
